import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Loader2,
  Shield,
  Users,
  Lock,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Layout } from '@/components/layout'
import { useToast } from '@/lib/hooks/use-toast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { useIsSuperAdmin } from '@/lib/hooks/usePermission'
import {
  getRoleById,
  updateRole,
  assignPermissionsToRole,
  deleteRole,
} from '@/lib/services/rbacService'
import { RoleFormDialog } from './RoleFormDialog'
import { PermissionSelector } from './PermissionSelector'
import type { UpdateRoleData } from '@/lib/services/rbacService'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { getUsersByRole } from '@/lib/services/rbacService'

export default function RoleDetailPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { roleId } = useParams({ strict: false }) as { roleId: string }

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isEditingPermissions, setIsEditingPermissions] = useState(false)
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([])
  const [isUsersExpanded, setIsUsersExpanded] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  // Permission checks
  const canViewRoles = useHasPermission('rbac.roles.view')
  const canManageRoles = useHasPermission('rbac.roles.manage')
  const canDeleteRoles = useHasPermission('rbac.roles.delete')
  const isSuperAdmin = useIsSuperAdmin()

  // Fetch role details
  const {
    data: role,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['role', roleId],
    queryFn: () => getRoleById(roleId),
    enabled: !!roleId && canViewRoles,
  })

  // Fetch users for this role (only when expanded)
  const { data: roleUsers, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['roleUsers', roleId],
    queryFn: () => getUsersByRole(roleId),
    enabled: !!roleId && isUsersExpanded && canViewRoles,
  })

  // Update role mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateRoleData) => updateRole(roleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role', roleId] })
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast({
        title: t('rbac.roleUpdated'),
        description: t('rbac.roleUpdatedSuccess'),
      })
      setIsEditDialogOpen(false)
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message || t('rbac.roleUpdateError'),
      })
    },
  })

  // Assign permissions mutation
  const assignPermissionsMutation = useMutation({
    mutationFn: (permissionIds: string[]) => assignPermissionsToRole(roleId, permissionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role', roleId] })
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast({
        title: t('rbac.permissionsUpdated'),
        description: t('rbac.permissionsUpdatedSuccess'),
      })
      setIsEditingPermissions(false)
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message || t('rbac.permissionsUpdateError'),
      })
    },
  })

  // Delete role mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast({
        title: t('rbac.roleDeleted'),
        description: t('rbac.roleDeletedSuccess'),
      })
      navigate({ to: '/admin/rbac/roles' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message || t('rbac.roleDeleteError'),
      })
    },
  })

  const handleUpdateRole = async (formData: { name: string; description: string }) => {
    await updateMutation.mutateAsync(formData)
  }

  const handleStartEditingPermissions = () => {
    if (role) {
      setSelectedPermissionIds(role.permissions.map(p => p.id))
      setIsEditingPermissions(true)
    }
  }

  const handleSavePermissions = async () => {
    await assignPermissionsMutation.mutateAsync(selectedPermissionIds)
  }

  const handleCancelEditingPermissions = () => {
    setIsEditingPermissions(false)
    if (role) {
      setSelectedPermissionIds(role.permissions.map(p => p.id))
    }
  }

  const handleDeleteRole = async () => {
    await deleteMutation.mutateAsync()
  }

  if (!canViewRoles) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">{t('common.accessDenied')}</h1>
            <p className="text-muted-foreground">{t('rbac.noPermissionToViewRoles')}</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
            <span className="sr-only">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    )
  }

  if (error || !role) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <div className="text-center py-12">
            <p className="text-destructive">{t('rbac.roleNotFound')}</p>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/admin/rbac/roles' })}
              className="mt-4"
            >
              {t('rbac.backToRoles')}
            </Button>
          </div>
        </div>
      </Layout>
    )
  }

  const hasChanges =
    isEditingPermissions &&
    JSON.stringify([...selectedPermissionIds].sort()) !==
      JSON.stringify(role.permissions.map(p => p.id).sort())

  const canEditRolePermissions = isSuperAdmin || (canManageRoles && !role.isSystem)

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate({ to: '/admin/rbac/roles' })}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('rbac.backToRoles')}
          </Button>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{role.name}</h1>
                {role.isSystem && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Shield className="h-3 w-3" aria-hidden="true" />
                    <span>{t('rbac.systemRole')}</span>
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground">{role.description}</p>
            </div>
            {canManageRoles && !role.isSystem && (
              <div className="flex gap-2">
                <Button onClick={() => setIsEditDialogOpen(true)}>
                  <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t('rbac.editRole')}
                </Button>
                {canDeleteRoles && !role.isSystem && (
                  <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('rbac.deleteRole')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                {t('rbac.permissions')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{role.permissions.length}</p>
              <p className="text-xs text-muted-foreground">{t('rbac.permissionsAssigned')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  {t('rbac.users')}
                </div>
                {role.userCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsUsersExpanded(!isUsersExpanded)}
                    className="h-8"
                  >
                    {isUsersExpanded ? (
                      <ChevronUp className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    )}
                    <span className="sr-only">
                      {isUsersExpanded ? 'Collapse' : 'Expand'} users list
                    </span>
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{role.userCount}</p>
              <p className="text-xs text-muted-foreground">{t('rbac.usersWithRole')}</p>

              {isUsersExpanded && (
                <div className="mt-4 pt-4 border-t">
                  {isLoadingUsers ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      <span className="sr-only">{t('common.loading')}</span>
                    </div>
                  ) : roleUsers && roleUsers.length > 0 ? (
                    <div className="space-y-2">
                      {roleUsers.map(user => (
                        <div
                          key={user.id}
                          className="flex flex-col gap-1 p-2 rounded-md bg-muted/50"
                        >
                          <p className="text-sm font-medium">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('admin.users.noUsers')}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Separator className="my-6" />

        {/* Permissions Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1">
              <h2 className="text-2xl font-semibold">{t('rbac.rolePermissions')}</h2>
              {isEditingPermissions && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Lock className="h-3 w-3" aria-hidden="true" />
                  {t('rbac.essentialPermissionsCannotBeRemoved')}
                </p>
              )}
            </div>
            {canEditRolePermissions && (
              <div className="flex gap-2">
                {isEditingPermissions ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleCancelEditingPermissions}
                      disabled={assignPermissionsMutation.isPending}
                    >
                      <X className="mr-2 h-4 w-4" aria-hidden="true" />
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleSavePermissions}
                      disabled={!hasChanges || assignPermissionsMutation.isPending}
                    >
                      {assignPermissionsMutation.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                      )}
                      {!assignPermissionsMutation.isPending && (
                        <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                      )}
                      {t('rbac.saveChanges')}
                    </Button>
                  </>
                ) : (
                  <Button onClick={handleStartEditingPermissions}>
                    <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('rbac.editPermissions')}
                  </Button>
                )}
              </div>
            )}
          </div>

          {isEditingPermissions ? (
            <PermissionSelector
              selectedPermissionIds={selectedPermissionIds}
              onPermissionsChange={setSelectedPermissionIds}
              disabled={assignPermissionsMutation.isPending}
            />
          ) : (
            <div className="space-y-6">
              {(() => {
                const translateGroupName = (group: string): string => {
                  const translationKey = `rbac.permissionGroups.${group}`
                  const translated = t(translationKey)
                  // If translation key not found, return the group name capitalized
                  if (translated === translationKey) {
                    return group.charAt(0).toUpperCase() + group.slice(1)
                  }
                  return translated
                }

                // Group permissions by first part of code before dot
                const groupedPermissions = role.permissions.reduce(
                  (acc, permission) => {
                    const group = permission.code.split('.')[0] || 'other'
                    if (!acc[group]) {
                      acc[group] = []
                    }
                    acc[group].push(permission)
                    return acc
                  },
                  {} as Record<string, typeof role.permissions>
                )

                return Object.entries(groupedPermissions).map(([group, permissions]) => (
                  <Card key={group}>
                    <CardHeader>
                      <div className="flex align-center justify-between">
                        <h3 className="text-lg">{translateGroupName(group)}</h3>

                        <p className="text-sm text-muted-foreground">
                          {permissions.length} permission{permissions.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[30%]">{t('rbac.permissionCode')}</TableHead>
                            <TableHead className="w-[40%]">{t('rbac.description')}</TableHead>
                            <TableHead className="w-[20%]">Action</TableHead>
                            <TableHead className="w-[10%] text-right">{t('rbac.type')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {permissions.map(permission => (
                            <TableRow key={permission.id}>
                              <TableCell className="font-mono text-sm">{permission.code}</TableCell>
                              <TableCell className="text-sm">{permission.description}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {permission.action || '—'}
                              </TableCell>
                              <TableCell className="text-right">
                                {permission.isEssential && (
                                  <Badge
                                    variant="outline"
                                    className="bg-blue-100 text-blue-700 border-blue-200 flex items-center gap-1 w-fit ml-auto"
                                  >
                                    <Lock className="h-3 w-3" aria-hidden="true" />
                                    <span className="text-xs">{t('rbac.essential')}</span>
                                  </Badge>
                                )}
                                {permission.isSystem && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs flex items-center gap-1 mt-1"
                                  >
                                    <Shield className="h-3 w-3" aria-hidden="true" />
                                    {t('rbac.system')} · {t('rbac.protected')}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))
              })()}
            </div>
          )}
        </div>

        {/* Edit Role Dialog */}
        <RoleFormDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleUpdateRole}
          role={role}
          mode="edit"
        />

        {/* Delete Role Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('rbac.deleteRoleConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {role.userCount > 0
                  ? t('rbac.roleHasUsers', { count: role.userCount })
                  : t('rbac.deleteRoleConfirmMessage', { roleName: role.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                {t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteRole}
                disabled={deleteMutation.isPending || role.userCount > 0}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {t('rbac.deleteRole')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  )
}
